/* eslint-disable prettier/prettier */

import { apiService } from './api.service';
import { ApiResponse } from '../types';

/**
 * Goal Type Interface
 * Matches backend GoalType entity structure
 */
export interface GoalType {
	id: string;
	name: string;
	description?: string;
	icon?: string;
	isactive: boolean;
	createdby: string;
	createdat: string;
	updatedat: string;
}

/**
 * Create Goal Type Payload
 */
export interface CreateGoalTypePayload {
	name: string;
	description?: string;
	icon?: string;
}

/**
 * Update Goal Type Payload
 */
export interface UpdateGoalTypePayload {
	name?: string;
	description?: string;
	icon?: string;
	isactive?: boolean;
}

/**
 * Goal Type Service
 * Handles all API communication related to goal types
 */
class GoalTypeService {
	/**
	 * Fetch all goal types
	 * By default only active goal types are fetched
	 *
	 * Backend Route:
	 * GET /api/goal-types
	 */
	async getGoalTypes(
		includeInactive: boolean = false
	): Promise<ApiResponse<GoalType[]>> {
		try {
			const response = await apiService.get<GoalType[]>('/goal-types', {
				includeinactive: includeInactive,
			});

			return response;
		} catch (error: any) {
			console.error('Error fetching goal types:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to fetch goal types'
			);
		}
	}

	/**
	 * Create a new goal type
	 *
	 * Backend Route:
	 * POST /api/goal-types
	 */
	async createGoalType(
		payload: CreateGoalTypePayload
	): Promise<ApiResponse<GoalType>> {
		try {
			const response = await apiService.post<GoalType>(
				'/goal-types',
				payload
			);

			return response;
		} catch (error: any) {
			console.error('Error creating goal type:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to create goal type'
			);
		}
	}

	/**
	 * Update an existing goal type
	 *
	 * Backend Route:
	 * PATCH /api/goal-types/:id
	 */
	// async updateGoalType(
	//     goalTypeId: string,
	//     payload: UpdateGoalTypePayload
	// ): Promise<ApiResponse<GoalType>> {
	//     try {
	//         /**
	//          * Using axios patch directly
	//          * since current apiService does not expose patch()
	//          */
	//         const response = await apiService.patch<GoalType>(
	//             `/api/goal-types/${goalTypeId}`,
	//             payload
	//         );

	//         return response.data;
	//     } catch (error: any) {
	//         console.error('Error updating goal type:', error);

	//         throw new Error(
	//             error?.response?.data?.message ||
	//             'Failed to update goal type'
	//         );
	//     }
	// }

	/**
	 * Soft delete / deactivate goal type
	 *
	 * Backend Route:
	 * DELETE /api/goal-types/:id
	 */
	async deleteGoalType(goalTypeId: string): Promise<ApiResponse<void>> {
		try {
			const response = await apiService.delete<void>(
				`/goal-types/${goalTypeId}`
			);

			return response;
		} catch (error: any) {
			console.error('Error deleting goal type:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to delete goal type'
			);
		}
	}
}

/**
 * Export singleton instance
 */
export const goalTypeService = new GoalTypeService();
